import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, FileText, Receipt, TrendingUp, Euro, Plus,
  ArrowUpRight, UserPlus, Target, CheckCircle, Calendar,
  TrendingDown, BarChart3, PieChart, Activity
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

import { API_URL } from '../../../config/api';

export default function AdminCRMDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentProspects, setRecentProspects] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [candidateStats, setCandidateStats] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchRevenueAnalytics();
    fetchInvoiceStats();
    fetchCandidateStats();
  }, []);

  const fetchDashboard = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/admin-crm/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        setRecentProspects(data.recent_prospects || []);
        setRecentQuotes(data.recent_quotes || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueAnalytics = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/analytics/revenue`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRevenueData(data);
      }
    } catch (error) {
      console.error('Error fetching revenue:', error);
    }
  };

  const fetchInvoiceStats = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/invoices/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoiceStats(data);
      }
    } catch (error) {
      console.error('Error fetching invoice stats:', error);
    }
  };

  const fetchCandidateStats = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/candidates/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCandidateStats(data);
      }
    } catch (error) {
      console.error('Error fetching candidate stats:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-crm-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tableau de Bord</h1>
          <p className="text-gray-500">Vue d'ensemble de ton activité BatiAssist</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/crm/prospects/new">
            <Button className="bg-blue-600 hover:bg-blue-700">
              <UserPlus className="w-4 h-4 mr-2" />
              Nouveau prospect
            </Button>
          </Link>
        </div>
      </div>

      {/* Main KPIs - Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl p-5 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-3">
            <Euro className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-90">CA Annuel</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(revenueData?.total_revenue || 0)}</p>
          <p className="text-xs opacity-75 mt-1">{revenueData?.year || new Date().getFullYear()}</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-5 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-90">CA Ce Mois</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(stats?.revenue_this_month || 0)}</p>
          <p className="text-xs opacity-75 mt-1">{stats?.invoices_paid_this_month || 0} factures payées</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-5 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-90">MRR (Revenus Récurrents)</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(stats?.mrr || 0)}</p>
          <p className="text-xs opacity-75 mt-1">{stats?.clients_active || 0} abonnés actifs</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl p-5 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-90">À Encaisser</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(invoiceStats?.total_pending || 0)}</p>
          <p className="text-xs opacity-75 mt-1">{invoiceStats?.pending || 0} en attente, {invoiceStats?.overdue || 0} en retard</p>
        </motion.div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm text-gray-500">Nouveaux</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.prospects_new || 0}</p>
          <p className="text-xs text-gray-400">{stats?.prospects_total || 0} prospects</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
              <FileText className="w-4 h-4 text-amber-600" />
            </div>
            <span className="text-sm text-gray-500">Devis</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.quotes_pending || 0}</p>
          <p className="text-xs text-green-500">{stats?.quotes_won || 0} gagnés</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <span className="text-sm text-gray-500">Clients</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.clients_active || 0}</p>
          <p className="text-xs text-gray-400">{stats?.clients_total || 0} total</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <Receipt className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="text-sm text-gray-500">Factures</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{invoiceStats?.paid || 0}</p>
          <p className="text-xs text-gray-400">{invoiceStats?.total || 0} total</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Users className="w-4 h-4 text-purple-600" />
            </div>
            <span className="text-sm text-gray-500">Candidats</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{candidateStats?.total || 0}</p>
          <p className="text-xs text-green-500">{candidateStats?.hired || 0} embauchés</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
              <Activity className="w-4 h-4 text-cyan-600" />
            </div>
            <span className="text-sm text-gray-500">Retenus</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{candidateStats?.retained || 0}</p>
          <p className="text-xs text-gray-400">{candidateStats?.interview_done || 0} entretiens</p>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Link to="/admin/crm/pipeline" className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-4 hover:shadow-lg transition-shadow">
          <Target className="w-6 h-6 mb-2" />
          <h3 className="font-semibold">Pipeline</h3>
          <p className="text-sm text-blue-100">Vue commerciale</p>
        </Link>
        <Link to="/admin/crm/prospects" className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-4 hover:shadow-lg transition-shadow">
          <UserPlus className="w-6 h-6 mb-2" />
          <h3 className="font-semibold">Prospects</h3>
          <p className="text-sm text-purple-100">Tous les leads</p>
        </Link>
        <Link to="/admin/crm/quotes" className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl p-4 hover:shadow-lg transition-shadow">
          <FileText className="w-6 h-6 mb-2" />
          <h3 className="font-semibold">Devis</h3>
          <p className="text-sm text-amber-100">Propositions</p>
        </Link>
        <Link to="/admin/crm/invoices" className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl p-4 hover:shadow-lg transition-shadow">
          <Receipt className="w-6 h-6 mb-2" />
          <h3 className="font-semibold">Factures</h3>
          <p className="text-sm text-emerald-100">Facturation</p>
        </Link>
        <Link to="/admin/resources/candidates" className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white rounded-xl p-4 hover:shadow-lg transition-shadow">
          <Users className="w-6 h-6 mb-2" />
          <h3 className="font-semibold">Candidats</h3>
          <p className="text-sm text-cyan-100">Espace RH</p>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Prospects */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Derniers prospects</h2>
            <Link to="/admin/crm/prospects" className="text-sm text-blue-600 hover:underline flex items-center">
              Voir tous <ArrowUpRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {recentProspects.length === 0 ? (
              <div className="p-4 text-center text-gray-400">Aucun prospect</div>
            ) : (
              recentProspects.slice(0, 5).map((prospect) => (
                <Link 
                  key={prospect.id} 
                  to={`/admin/crm/prospects/${prospect.id}`}
                  className="p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-semibold">{prospect.name?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{prospect.name}</p>
                    <p className="text-sm text-gray-500 truncate">{prospect.company_name || prospect.email}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      prospect.source === 'diagnostic' ? 'bg-purple-100 text-purple-600' :
                      prospect.source === 'contact' ? 'bg-blue-100 text-blue-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {prospect.source === 'diagnostic' ? 'Diagnostic' : 
                       prospect.source === 'contact' ? 'Contact' : prospect.source}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(prospect.created_at)}</p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Recent Quotes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Derniers devis</h2>
            <Link to="/admin/crm/quotes" className="text-sm text-blue-600 hover:underline flex items-center">
              Voir tous <ArrowUpRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {recentQuotes.length === 0 ? (
              <div className="p-4 text-center text-gray-400">Aucun devis</div>
            ) : (
              recentQuotes.slice(0, 5).map((quote) => (
                <Link 
                  key={quote.id} 
                  to={`/admin/crm/quotes/${quote.id}`}
                  className="p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{quote.quote_number}</p>
                    <p className="text-sm text-gray-500 truncate">{quote.contact_name}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-semibold text-gray-900">{formatCurrency(quote.total)}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      quote.status === 'accepted' ? 'bg-green-100 text-green-600' :
                      quote.status === 'sent' ? 'bg-blue-100 text-blue-600' :
                      quote.status === 'refused' ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {quote.status === 'draft' ? 'Brouillon' :
                       quote.status === 'sent' ? 'Envoyé' :
                       quote.status === 'accepted' ? 'Accepté' :
                       quote.status === 'refused' ? 'Refusé' : quote.status}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
